import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
import { SHARED_HEALTH_SCORING_RULES } from '@process-advisor/process-miner/consultant/consultantSharedPromptRules';

export const buildQuickHealthcheckPrompt = (args = {}) => {
  const inputPayload =
    args.inputPayload ||
    buildSharedConsultantPayload({
      issueTypeStr: args.issueTypeStr,
      metricsHistory: args.metricsHistory,
      metricsTotal: args.metricsTotal,
      transitionsJSON: args.transitionsJSON,
      variantsJSON: args.variantsJSON,
      sampleItems: args.sampleItems,
      fieldUsage: args.fieldUsage,
      statusTypeSummary: args.statusTypeSummary,
      statusTypeMappings: args.statusTypeMappings,
      activityType: args.activityType,
      activityContext: args.activityContext,
      sourceSystem: args.sourceSystem,
      appVersion: args.appVersion,
    });

  const inputText = JSON.stringify(inputPayload, null, 2);
  const reportScopeText = buildReportScopePromptSection(args.reportScopeSummary);

  return `You are an expert process consultant producing a Quick Healthcheck for a new engagement.${reportScopeText}

This is a short-form read, not a deep assessment. A consultant will use this output to frame a 30-minute conversation with a client. Keep it sharp, evidence-grounded, and immediately usable.

CANONICAL EVIDENCE RULES
- The JSON evidence payload is the same canonical Advisor evidence payload used across all Advisor AI content. Use it as the single source of truth.
- workload_signals.attribute_usage is the authoritative attribute/workload segmentation signal. Derive sparse/highly-used/classification conclusions from it rather than expecting separate duplicated sections.
- sample_evidence.rows contains random/scoped sample work items after report-scope and privacy rules. Use sample rows only when item-level examples, validation, or data-quality checks are necessary. Do not let sample rows override aggregate process metrics, variants, transitions, SLA signals, or attribute usage.

ACTIVITY CONTEXT RULES
- The mined activity field may not be Status. Always read activity_context before interpreting paths, transitions, or activity values.
- Use activity_context.activity_label to describe what the process map represents.
- If activity_kind is routing_group, interpret transitions as group routing, queue ownership, escalation, or handoff movement — not lifecycle status progression.
- If activity_kind is ownership, interpret transitions as assignee/owner changes and reassignment churn — not status progression.
- If activity_kind is lifecycle_status, lifecycle/status language is appropriate.
- If activity_kind is classification, approval_state, custom_activity_field, or unknown, avoid calling activity values "statuses" unless the field is explicitly status/state.
- Prefer neutral words such as activity, step, value, path, transition, handoff, routing, ownership, or lifecycle according to the context.


CONTROLLED ACTIVITY TYPE RULES
- The payload contains activity_context.analysis_type / activity_context.activity_kind. Treat this as the controlling interpretation, not merely a hint.
- Only use lifecycle/status evidence such as queue time, wait time, touch time, admin time, flow efficiency, status bottlenecks, or status progression when activity_kind is lifecycle_status.
- If activity_kind is routing_group, describe elapsed time as resolver-group dwell time, routing delay, escalation delay, handoff delay, or ownership dwell — not literal waiting in queues.
- If activity_kind is ownership, describe elapsed time as assignee dwell time, reassignment delay, ownership churn, or workload concentration — not status queue time.
- If activity_kind is classification, interpret transitions as classification changes, escalation/de-escalation, or data-quality movement. Do not describe this as workflow progress.
- If activity_kind is approval_state, interpret transitions as approval movement, approval dwell, rejection/rework, or governance delay.
- Respect activity_context.invalid_interpretations and activity_context.valid_metric_families when selecting evidence and wording.

GENERAL REASONING RULES
- Use the strongest available evidence from the payload.
- If a model is missing, invalid, or unavailable, do not rely on it.
- Distinguish clearly between what the data shows and what that likely means.
- Do not fabricate precision, thresholds, or business impacts not supported by the data.
- Use specific metrics and named transitions or paths when available.
- Use plain text only. Do not return markdown fences or commentary outside the JSON.
- No ROI, no cost framing, no financial figures. This is a read, not a business case.

OVERALL HEALTH RULES
- Provide a single health score from 1 to 10 with a short rating label and a one-sentence rationale.
- The score should reflect the strongest available drivers across delay, rework, predictability, structural issues, and workload signals.
- Confidence should reflect evidence coverage.

WHAT'S WORKING RULES
- Provide 2 to 4 concrete things that are working well in the process.
- Each item must be evidence-based, not generic praise.
- Cite specific metrics, paths, or patterns from the data.
- Titles: punchy, under 6 words where possible, action-oriented (e.g. "Triage is fast", not "The triage process appears to be fast").
- Evidence: one short sentence, under 20 words. State the specific data point.

WHAT'S NOT WORKING RULES
- Provide 3 to 5 clear issues that stand out in the evidence.
- Titles: punchy, under 6 words where possible, name the problem (e.g. "Rework at review step", not "There is a concerning amount of rework").
- Evidence: one short sentence with the specific data point, under 20 words.
- Interpretation: one short sentence on what that likely means operationally, under 20 words. Skip if the evidence is self-explanatory.
- Prefer issues with high volume, significant delay, strong rework, poor predictability, or weak classification.

QUICK WINS RULES
- Provide 2 to 4 actions the customer could take in the next 30 to 60 days.
- These must be practical and proportionate — not transformation programs.
- Titles: punchy, under 6 words. Frame as the action (e.g. "Clarify review criteria", not "Implement a criteria clarification initiative").
- Action: one short sentence on what to do, under 20 words.
- Expected benefit: one short sentence, under 15 words. Be specific where possible (e.g. "Cut review rework 30-50% in two sprints"), not generic ("improve quality").
- Prefer actions that do not require new tools, large budgets, or cross-team initiatives.

CONSULTANT NOTE RULE
- End with a short consultant note (2 to 3 sentences) that frames what the consultant should say in opening the client conversation.
- Make it data-specific, not generic.
- Tailor language and priorities to any organization context supplied.

SCORING RULES
${SHARED_HEALTH_SCORING_RULES}

OUTPUT FORMAT
Return exactly one JSON object with this exact structure (replace all values with real content derived from the input evidence). The score must reflect your own overall judgement from the full evidence and stay aligned with the tone of the assessment narrative:
{
  "overall_health": { "score": 7, "rating": "", "confidence": "High|Moderate|Low", "rationale": "" },
  "whats_working": [
    { "title": "", "evidence": "" }
  ],
  "whats_not_working": [
    { "title": "", "evidence": "", "interpretation": "" }
  ],
  "quick_wins": [
    { "title": "", "action": "", "expected_benefit": "" }
  ],
  "consultant_note": "",
  "data_coverage": { "process_metrics": true, "transitions": true, "variants": true, "field_usage": true, "notes": [] }
}

FIELD POPULATION RULE
- Do not infer that records are missing a field merely because the listed top values cover less than 100% of tasks.
- "top_values", "values", "value_coverage_pct", and value distributions may be truncated/ranked summaries.
- Only call a field incomplete when "populated_pct", "population_rate", "missing_count", or explicit sample evidence shows missing/blank values.
- Treat a literal value such as "None" as a real classification value unless the evidence explicitly identifies it as blank/null/missing.

INPUT EVIDENCE
${inputText}`;
};

export default buildQuickHealthcheckPrompt;
